import React, { useState, useEffect, useRef } from 'react';
import { dealsApi, documentsApi, usersApi } from '../services/api';
import {
  Building2,
  Plus,
  Search,
  Calendar,
  DollarSign,
  CheckSquare,
  FileText,
  Users,
  File,
  Clock,
  ChevronDown,
  X,
  FolderOpen,
  ArrowLeft,
  AlertCircle,
  Trash2,
  Edit3,
  ChevronRight,
  UserCircle,
  StickyNote,
  Send,
  TrendingUp,
  MapPin,
  Award,
  Target
} from 'lucide-react';
import './Deals.css';
import DiligenceTracker from './DiligenceTracker';



// ─── Comprehensive industry list ─────────────────────────
const INDUSTRIES = [
  // Agriculture & Natural Resources
  'Agriculture','Agritech','Farming','Aquaculture','Forestry','Fisheries',
  'Food Production','Crop Production','Livestock','Agricultural Equipment',
  'Agricultural Chemicals','Precision Agriculture','Vertical Farming',
  // Energy & Utilities
  'Oil & Gas','Renewable Energy','Solar Energy','Wind Energy','Hydropower',
  'Nuclear Energy','Energy Storage','Energy Infrastructure','Energy Trading',
  'Utilities','Smart Grid','Battery Technology','Hydrogen Energy','Biofuels',
  // Technology
  'Software','SaaS','Artificial Intelligence','Machine Learning',
  'Data Analytics','Big Data','Cloud Computing','Cybersecurity','DevOps',
  'IT Services','Hardware','Semiconductor','Robotics',
  'Internet of Things (IoT)','Quantum Computing','Blockchain','Web3',
  'Metaverse','Augmented Reality','Virtual Reality',
  // Financial Services
  'Banking','Fintech','Payments','Lending','Insurance','Insurtech',
  'Wealth Management','Asset Management','Hedge Funds','Venture Capital',
  'Private Equity','Cryptocurrency','Digital Assets','Capital Markets',
  'Mortgage Services','Credit Services',
  // Healthcare & Life Sciences
  'Healthcare','Healthtech','Biotechnology','Pharmaceuticals',
  'Medical Devices','Diagnostics','Telemedicine','Digital Health',
  'Healthcare IT','Genomics','Bioinformatics','Clinical Research',
  'Medical Research','Hospital Systems','Elder Care','Mental Health',
  // Consumer & Retail
  'Retail','E-commerce','Consumer Goods','Consumer Electronics',
  'Luxury Goods','Fashion & Apparel','Footwear','Beauty & Cosmetics',
  'Personal Care','Household Goods','Subscription Commerce',
  'Direct-to-Consumer (DTC)','Marketplaces',
  // Food & Beverage
  'Food & Beverage','Food Processing','Restaurants','Food Delivery',
  'Beverage Production','Alcoholic Beverages','Non-Alcoholic Beverages',
  'Specialty Foods','Packaged Foods','Nutrition','Plant-Based Foods',
  // Manufacturing & Industrial
  'Manufacturing','Industrial Equipment','Advanced Manufacturing',
  'Additive Manufacturing (3D Printing)','Industrial Automation',
  'Industrial Robotics','Chemicals','Specialty Chemicals','Plastics',
  'Metals & Mining','Materials Science',
  // Transportation & Mobility
  'Transportation','Logistics','Supply Chain','Freight','Shipping',
  'Trucking','Rail','Aviation','Aerospace','Automotive',
  'Electric Vehicles','Autonomous Vehicles','Fleet Management',
  'Last-Mile Delivery','Mobility Services',
  // Real Estate & Construction
  'Real Estate','Proptech','Commercial Real Estate',
  'Residential Real Estate','Real Estate Development',
  'Property Management','Construction','Construction Technology',
  'Infrastructure','Building Materials','Smart Buildings',
  // Media & Entertainment
  'Media','Entertainment','Film & Television','Streaming','Gaming',
  'Esports','Music','Publishing','Digital Media','Advertising',
  'Adtech','Content Creation',
  // Education
  'Education','EdTech','Online Learning','Higher Education',
  'K-12 Education','Corporate Training','Professional Certification',
  'Learning Platforms',
  // Hospitality & Travel
  'Hospitality','Hotels & Resorts','Travel','Tourism',
  'Vacation Rentals','Travel Technology','Cruise Lines','Event Management',
  // Telecommunications
  'Telecommunications','Wireless','Broadband','Satellite Communications',
  '5G Infrastructure','Network Infrastructure',
  // Government & Public Sector
  'Government Services','GovTech','Defense','Military Technology',
  'Public Infrastructure','Smart Cities',
  // Environmental & Sustainability
  'Climate Tech','Sustainability','Carbon Capture','Waste Management',
  'Recycling','Water Technology','Environmental Services','ESG Solutions',
  // Professional Services
  'Consulting','Legal Services','Accounting','Marketing Services',
  'Recruitment','HR Services','Outsourcing','Managed Services',
  // Sports & Recreation
  'Sports','Fitness','Sports Technology','Gyms & Fitness Centers',
  'Outdoor Recreation','Sporting Goods',
  // Emerging
  'Creator Economy','Influencer Marketing','Digital Identity',
  'Space Technology','Drone Technology','Pet Industry',
  'Dating Platforms','Social Networks','Market Research',
  'Community Platforms',
];

// ─── Industry typeahead input ────────────────────────────
function IndustryInput({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handle = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = query.length >= 1
    ? INDUSTRIES.filter(i => i.toLowerCase().includes(query.toLowerCase())).slice(0, 10)
    : [];

  const handleChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const handleSelect = (industry) => {
    setQuery(industry);
    onChange(industry);
    setOpen(false);
  };

  return (
    <div className="industry-input-wrapper" ref={containerRef}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => filtered.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Type to search industries…'}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="industry-dropdown">
          {filtered.map(ind => (
            <button
              key={ind}
              className="industry-option"
              onMouseDown={e => { e.preventDefault(); handleSelect(ind); }}
            >
              {ind}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const stages = [
  { id: 'level_1_assessment',    name: 'Level 1 Assessment',    color: '#6B7280' },
  { id: 'level_2_assessment',    name: 'Level 2 Assessment',    color: '#F59E0B' },
  { id: 'loi',                   name: 'LOI',                   color: '#3B82F6' },
  { id: 'final_agreements',      name: 'Final Agreements',      color: '#8B5CF6' },
  { id: 'closed',                name: 'Closed',                color: '#10B981' },
  { id: 'post_close_integration',name: 'Post Close Integration',color: '#0EA5E9' },
  { id: 'deal_closed',           name: 'Deal Closed',           color: '#059669' },
];

const DEAL_TYPES = [
  { value: '', label: 'Select type…' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'merger', label: 'Merger' },
  { value: 'minority_stake', label: 'Minority Stake' },
  { value: 'asset_purchase', label: 'Asset Purchase' },
  { value: 'management_buyout', label: 'Management Buyout (MBO)' },
  { value: 'leveraged_buyout', label: 'Leveraged Buyout (LBO)' },
  { value: 'recapitalization', label: 'Recapitalization' },
  { value: 'joint_venture', label: 'Joint Venture' },
];

const DEAL_SOURCES = [
  { value: '', label: 'Select source…' },
  { value: 'proprietary', label: 'Proprietary' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'broker', label: 'Broker / Banker' },
  { value: 'referral', label: 'Referral' },
  { value: 'conference', label: 'Conference / Event' },
  { value: 'lp_network', label: 'LP Network' },
  { value: 'co_investor', label: 'Co-Investor' },
  { value: 'other', label: 'Other' },
];

const OUTCOME_REASONS = {
  closed: ['Full acquisition', 'Minority investment', 'Partnership agreement', 'Asset purchase'],
  passed: ['Valuation too high', 'Diligence findings', 'Market conditions', 'Strategic misfit', 'Capacity constraints', 'Management issues'],
  lost_competitor: ['Outbid by strategic', 'Outbid by financial sponsor', 'Seller preferred other terms', 'Process ended'],
  deal_died: ['Seller withdrew', 'Regulatory issues', 'Financing fell through', 'Material adverse change'],
};

const ARCHIVE_STAGES = ['archived', 'discontinued'];

// Normalise legacy stage IDs from old data so existing deals still resolve
function normaliseStage(stageId) {
  const legacyMap = {
    initial_review: 'level_1_assessment',
    screening: 'level_2_assessment',
    due_diligence: 'loi',
    negotiation: 'final_agreements',
    closing: 'closed',
    archived: 'closed',
    discontinued: 'closed',
  };
  return legacyMap[stageId] || stageId || 'level_1_assessment';
}

function getStage(stageId) {
  return stages.find(s => s.id === normaliseStage(stageId)) || stages[0];
}

// ─────────────────────────────────────────────
// Main Deals component
// ─────────────────────────────────────────────
function Deals({ onOpenAnnotate, deals = [], setDeals, initialDeal, onInitialDealConsumed, currentUser, logAudit }) {
  const [documents, setDocuments] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);

  // Create deal form
  const [newDealName, setNewDealName] = useState('');
  const [newDealIndustry, setNewDealIndustry] = useState('');
  const [newDealValue, setNewDealValue] = useState('');
  const [newDealTargetClose, setNewDealTargetClose] = useState('');
  const [newDealStage, setNewDealStage] = useState('level_1_assessment');
  const [newDealOwner, setNewDealOwner] = useState('');
  const [newDealType, setNewDealType] = useState('');
  const [newDealSource, setNewDealSource] = useState('');
  const [newDealGeography, setNewDealGeography] = useState('');

  // Outcome exit modal
  const [outcomeModal, setOutcomeModal] = useState(null); // { deal, targetStage }

  useEffect(() => { fetchSupportingData(); }, []);

  // Open a deal passed in from Pipeline navigation
  useEffect(() => {
    if (initialDeal) {
      setSelectedDeal(initialDeal);
      onInitialDealConsumed?.();
    }
  }, [initialDeal]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSupportingData = async () => {
    try {
      const [docsRes, usersRes] = await Promise.all([
        documentsApi.list(),
        usersApi.list().catch(() => ({ data: [] })),
      ]);
      // Map Supabase fields to frontend format
      const mappedDocs = (docsRes.data.documents || []).map(doc => ({
        jobId: doc.id,
        filename: doc.filename,
        category: doc.category,
        dealId: doc.deal_id,
        dealName: doc.deal_name,
        aiProcessingStatus: doc.ai_processing_status,
        createdAt: doc.created_at,
        completedAt: doc.updated_at
      }));
      setDocuments(mappedDocs);
      setOrgUsers(usersRes.data || []);
    } catch (err) {
      console.error('Failed to fetch supporting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const [createErrors, setCreateErrors] = useState({});

  const validateCreateDeal = () => {
    const errs = {};
    if (!newDealName.trim())    errs.name      = 'Deal name is required.';
    if (!newDealType)           errs.dealType  = 'Deal type is required.';
    if (!newDealSource)         errs.dealSource= 'Deal source is required.';
    if (!newDealIndustry.trim())errs.industry  = 'Industry is required.';
    return errs;
  };

  const handleCreateDeal = async () => {
    const errs = validateCreateDeal();
    if (Object.keys(errs).length) { setCreateErrors(errs); return; }
    setCreateErrors({});
    try {
      const res = await dealsApi.create({
        name: newDealName,
        industry: newDealIndustry,
        value: newDealValue ? parseFloat(newDealValue) : null,
        targetClose: newDealTargetClose,
        stage: newDealStage,
        owner: newDealOwner || null,
        dealType: newDealType || '',
        dealSource: newDealSource || '',
        geography: newDealGeography || '',
      });
      setDeals(prev => [...prev, res.data]);
      if (logAudit) logAudit('deal_created', 'deals', `Created deal: ${newDealName}`);
      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to create deal:', err);
      alert('Failed to create deal. Is the backend server running?');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      const deletedDeal = deals.find(d => d.id === dealId);
      await dealsApi.delete(dealId);
      if (logAudit) logAudit('deal_deleted', 'deals', `Deleted deal: ${deletedDeal?.name || dealId}`);
      setDeals(prev => prev.filter(d => d.id !== dealId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete deal:', err);
    }
  };

  const handleMoveStage = async (deal, newStageId) => {
    try {
      const res = await dealsApi.update(deal.id, { stage: newStageId });
      if (logAudit) logAudit('deal_status_changed', 'deals', `"${deal.name}" moved to ${newStageId.replace(/_/g, ' ')}`);
      setDeals(prev => prev.map(d => d.id === deal.id ? res.data : d));
    } catch (err) {
      console.error('Failed to move deal:', err);
    }
  };

  const handleUpdateDeal = async (dealId, updates) => {
    try {
      const res = await dealsApi.update(dealId, updates);
      setDeals(prev => prev.map(d => d.id === dealId ? res.data : d));
      // Keep selectedDeal in sync
      setSelectedDeal(res.data);
    } catch (err) {
      console.error('Failed to update deal:', err);
    }
  };

  const resetForm = () => {
    setNewDealName('');
    setNewDealIndustry('');
    setNewDealValue('');
    setNewDealTargetClose('');
    setNewDealStage('level_1_assessment');
    setNewDealOwner('');
    setNewDealType('');
    setNewDealSource('');
    setNewDealGeography('');
  };

  // Intercept stage moves to archive/discontinued — open outcome modal
  const handleMoveStageWithOutcome = (deal, newStageId) => {
    if (ARCHIVE_STAGES.includes(newStageId)) {
      setOutcomeModal({ deal, targetStage: newStageId });
    } else {
      handleMoveStage(deal, newStageId);
    }
  };

  const handleOutcomeSubmit = async (deal, targetStage, outcomeData) => {
    try {
      const res = await dealsApi.update(deal.id, {
        stage: targetStage,
        outcome: outcomeData.outcome,
        outcomeReason: outcomeData.outcomeReason,
        outcomeDetails: outcomeData.outcomeDetails,
        finalValue: outcomeData.finalValue ? parseFloat(outcomeData.finalValue) : null,
        dealQualityScore: outcomeData.dealQualityScore || null,
        teamNotes: outcomeData.teamNotes,
        archivedAt: new Date().toISOString(),
      });
      if (logAudit) logAudit('deal_outcome_captured', 'deals', `"${deal.name}" → ${outcomeData.outcome}`);
      setDeals(prev => prev.map(d => d.id === deal.id ? res.data : d));
    } catch (err) {
      console.error('Failed to save outcome:', err);
    }
    setOutcomeModal(null);
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      notation: 'compact', maximumFractionDigits: 1,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const getStageBadge = (stageId) => {
    const stage = getStage(stageId);
    return (
      <span className="deal-stage-badge" style={{
        backgroundColor: `${stage.color}18`,
        color: stage.color,
        border: `1px solid ${stage.color}30`,
      }}>
        {stage.name}
      </span>
    );
  };

  const filteredDeals = deals.filter(deal => {
    const matchesSearch =
      deal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.industry?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === 'all' || normaliseStage(deal.stage) === stageFilter;
    return matchesSearch && matchesStage;
  });

  // ── Deal Detail view
  if (selectedDeal) {
    // Keep in sync with latest data
    const latestDeal = deals.find(d => d.id === selectedDeal.id) || selectedDeal;
    return (
      <DealDetail
        deal={latestDeal}
        onBack={() => setSelectedDeal(null)}
        documents={documents}
        allDeals={deals}
        orgUsers={orgUsers}
        onOpenAnnotate={onOpenAnnotate}
        onUpdateDeal={handleUpdateDeal}
        currentUser={currentUser}
      />
    );
  }

  if (loading) {
    return (
      <div className="deals-container">
        <div className="deals-loading">
          <div className="deals-spinner" />
          <p>Loading deals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deals-container">
      {/* Header */}
      <div className="deals-header">
        <div className="deals-title-section">
          <h1 className="deals-title">Deals</h1>
          <span className="deals-count">{deals.length} deal{deals.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="deals-header-actions">
          <button className="deals-new-btn" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /><span>New Deal</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="deals-filters">
        <div className="deals-search">
          <Search size={16} className="deals-search-icon" />
          <input type="text" placeholder="Search deals..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="deals-filter-dropdown">
          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="all">All stages</option>
            {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <ChevronDown size={16} className="deals-filter-chevron" />
        </div>
      </div>

      {/* Deals list */}
      {deals.length === 0 ? (
        <div className="deals-empty">
          <div className="deals-empty-icon"><FolderOpen size={48} /></div>
          <h3>No deals yet</h3>
          <p>Create your first deal to get started</p>
          <button className="deals-new-btn" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /><span>Create Deal</span>
          </button>
        </div>
      ) : (
        <div className="deals-table-container">
          <table className="deals-table">
            <thead>
              <tr>
                <th className="deals-th-name">Deal Name</th>
                <th className="deals-th-industry">Industry</th>
                <th className="deals-th-stage">Stage</th>
                <th className="deals-th-value">Value</th>
                <th className="deals-th-close">Target Close</th>
                <th className="deals-th-updated">Updated</th>
                <th className="deals-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map(deal => (
                <tr key={deal.id} className="deals-row" onClick={() => setSelectedDeal(deal)}>
                  <td className="deals-td-name">
                    <div className="deals-name-cell">
                      <div className="deals-avatar">{deal.name?.charAt(0).toUpperCase()}</div>
                      <span className="deals-name-text">{deal.name}</span>
                    </div>
                  </td>
                  <td className="deals-td-industry">{deal.industry || '—'}</td>
                  <td className="deals-td-stage">{getStageBadge(deal.stage)}</td>
                  <td className="deals-td-value">{formatCurrency(deal.value)}</td>
                  <td className="deals-td-close">{formatDate(deal.targetClose)}</td>
                  <td className="deals-td-updated">{formatDate(deal.updatedAt || deal.createdAt)}</td>
                  <td className="deals-td-actions">
                    <button className="deals-delete-btn"
                      onClick={e => { e.stopPropagation(); setDeleteConfirm(deal); }}
                      title="Delete deal">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Deal Modal */}
      {showCreateModal && (
        <div className="deals-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="deals-modal" onClick={e => e.stopPropagation()}>
            <div className="deals-modal-header">
              <h2>Create New Deal</h2>
              <button className="deals-modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="deals-modal-body">
              <div className="deals-form-group">
                <label>Deal Name *</label>
                <input
                  type="text" placeholder="Enter deal name"
                  value={newDealName}
                  onChange={e => { setNewDealName(e.target.value); setCreateErrors(p => ({ ...p, name: '' })); }}
                  className={createErrors.name ? 'input-error' : ''}
                  autoFocus
                />
                {createErrors.name && <span className="deals-field-error">{createErrors.name}</span>}
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Deal Type *</label>
                  <select
                    value={newDealType}
                    onChange={e => { setNewDealType(e.target.value); setCreateErrors(p => ({ ...p, dealType: '' })); }}
                    className={createErrors.dealType ? 'input-error' : ''}
                  >
                    {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {createErrors.dealType && <span className="deals-field-error">{createErrors.dealType}</span>}
                </div>
                <div className="deals-form-group">
                  <label>Deal Source *</label>
                  <select
                    value={newDealSource}
                    onChange={e => { setNewDealSource(e.target.value); setCreateErrors(p => ({ ...p, dealSource: '' })); }}
                    className={createErrors.dealSource ? 'input-error' : ''}
                  >
                    {DEAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  {createErrors.dealSource && <span className="deals-field-error">{createErrors.dealSource}</span>}
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Industry *</label>
                  <IndustryInput
                    value={newDealIndustry}
                    onChange={v => { setNewDealIndustry(v); setCreateErrors(p => ({ ...p, industry: '' })); }}
                    placeholder="Type to search industries…"
                  />
                  {createErrors.industry && <span className="deals-field-error">{createErrors.industry}</span>}
                </div>
                <div className="deals-form-group">
                  <label>Geography</label>
                  <input type="text" placeholder="Country / Region"
                    value={newDealGeography} onChange={e => setNewDealGeography(e.target.value)} />
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Stage</label>
                  <select value={newDealStage} onChange={e => setNewDealStage(e.target.value)}>
                    {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="deals-form-group">
                  <label>Owner</label>
                  <select value={newDealOwner} onChange={e => setNewDealOwner(e.target.value)}>
                    <option value="">Unassigned</option>
                    {orgUsers.map(u => (
                      <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="deals-form-row">
                <div className="deals-form-group">
                  <label>Deal Value</label>
                  <div className="deals-input-with-icon">
                    <DollarSign size={16} className="deals-input-icon" />
                    <input type="number" placeholder="Enter deal value"
                      value={newDealValue} onChange={e => setNewDealValue(e.target.value)} />
                  </div>
                </div>
                <div className="deals-form-group">
                  <label>Target Close</label>
                  <input type="date" value={newDealTargetClose}
                    onChange={e => setNewDealTargetClose(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="deals-modal-footer">
              <button className="deals-btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); setCreateErrors({}); }}>
                Cancel
              </button>
              <button className="deals-btn-primary" onClick={handleCreateDeal}>
                Create Deal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Outcome Exit Modal */}
      {outcomeModal && (
        <OutcomeExitModal
          deal={outcomeModal.deal}
          targetStage={outcomeModal.targetStage}
          onSubmit={handleOutcomeSubmit}
          onCancel={() => setOutcomeModal(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="deals-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="deals-modal deals-modal-small" onClick={e => e.stopPropagation()}>
            <div className="deals-modal-header">
              <h2>Delete Deal</h2>
              <button className="deals-modal-close" onClick={() => setDeleteConfirm(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="deals-modal-body">
              <p className="deals-confirm-text">
                Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?
              </p>
              <p className="deals-confirm-subtext">
                This action cannot be undone.
              </p>
            </div>
            <div className="deals-modal-footer">
              <button className="deals-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="deals-btn-danger" onClick={() => handleDeleteDeal(deleteConfirm.id)}>
                Delete Deal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Kanban Board
// ─────────────────────────────────────────────
function KanbanBoard({ deals, stages, onSelectDeal, onDeleteDeal, onMoveStage, formatCurrency, formatDate }) {
  return (
    <div className="kanban-board">
      {stages.map(stage => {
        const stageDeals = deals.filter(d => normaliseStage(d.stage) === stage.id);
        return (
          <div key={stage.id} className="kanban-column">
            <div className="kanban-column-header" style={{ borderTop: `3px solid ${stage.color}` }}>
              <span className="kanban-column-title">{stage.name}</span>
              <span className="kanban-column-count"
                style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
                {stageDeals.length}
              </span>
            </div>
            <div className="kanban-column-body">
              {stageDeals.length === 0
                ? <div className="kanban-empty-col">No deals</div>
                : stageDeals.map(deal => (
                  <KanbanCard
                    key={deal.id}
                    deal={deal}
                    stage={stage}
                    stages={stages}
                    onSelect={() => onSelectDeal(deal)}
                    onDelete={() => onDeleteDeal(deal)}
                    onMoveStage={onMoveStage}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                  />
                ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ deal, stage, stages, onSelect, onDelete, onMoveStage, formatCurrency, formatDate }) {
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  return (
    <div className="kanban-card" onClick={onSelect}>
      <div className="kanban-card-header">
        <div className="kanban-card-avatar"
          style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
          {deal.name?.charAt(0).toUpperCase()}
        </div>
        <div className="kanban-card-actions">
          {/* Move stage button */}
          <div className="kanban-move-wrapper">
            <button className="kanban-card-move"
              onClick={e => { e.stopPropagation(); setShowMoveMenu(p => !p); }}
              title="Move to stage">
              <ChevronRight size={13} />
            </button>
            {showMoveMenu && (
              <div className="kanban-move-menu" onClick={e => e.stopPropagation()}>
                <div className="kanban-move-menu-title">Move to</div>
                {stages.filter(s => s.id !== normaliseStage(deal.stage)).map(s => (
                  <button key={s.id} className="kanban-move-option"
                    onClick={() => { onMoveStage(deal, s.id); setShowMoveMenu(false); }}>
                    <span className="kanban-move-dot" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="kanban-card-delete"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            title="Delete deal">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="kanban-card-name">{deal.name}</div>
      {deal.industry && (
        <div className="kanban-card-industry">
          <Building2 size={12} /><span>{deal.industry}</span>
        </div>
      )}
      <div className="kanban-card-meta">
        {deal.value && (
          <span className="kanban-card-value">
            <DollarSign size={12} />{formatCurrency(deal.value)}
          </span>
        )}
        {deal.targetClose && (
          <span className="kanban-card-close">
            <Calendar size={12} />{formatDate(deal.targetClose)}
          </span>
        )}
      </div>
      {deal.owner && (
        <div className="kanban-card-owner">
          <UserCircle size={12} /><span>{deal.owner}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Deal Detail
// ─────────────────────────────────────────────
function DealDetail({ deal, onBack, documents, allDeals, orgUsers, onOpenAnnotate, onUpdateDeal, currentUser }) {
  const [activeTab, setActiveTab] = useState('tasks');
  const [dealDocuments, setDealDocuments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDealData(); }, [deal.id]);

  const fetchDealData = async () => {
    try {
      const [docsRes, tasksRes, contactsRes] = await Promise.all([
        dealsApi.getDocuments(deal.id),
        dealsApi.getTasks(deal.id).catch(() => ({ data: [] })),
        dealsApi.getContacts(deal.id).catch(() => ({ data: [] })),
      ]);
      setDealDocuments(docsRes.data || []);
      setTasks(tasksRes.data || []);
      setContacts(contactsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch deal data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      notation: 'compact', maximumFractionDigits: 1,
    }).format(value);
  };

  const stage = getStage(deal.stage);

  const tabs = [
    { id: 'tasks',        label: 'Tasks',           icon: CheckSquare, count: tasks.length },
    { id: 'documents',    label: 'Documents',        icon: FileText,    count: dealDocuments.length },
    { id: 'contacts',     label: 'Contacts',         icon: Users,       count: contacts.length },
    { id: 'diligence',    label: 'Info Requests',    icon: AlertCircle, count: null },
    { id: 'notes',        label: 'Notes',            icon: StickyNote,  count: null },
    { id: 'intelligence', label: 'Intelligence',     icon: TrendingUp,  count: null },
    { id: 'edit',         label: 'Edit Deal',        icon: Edit3,       count: null },
  ];

  return (
    <div className="deal-detail-container">
      <div className="deal-detail-header">
        <button className="deal-detail-back" onClick={onBack}>
          <ArrowLeft size={18} /><span>Back to Pipeline</span>
        </button>
      </div>

      <div className="deal-detail-card">
        <div className="deal-detail-title-row">
          <div className="deal-detail-title-section">
            <span className="deal-detail-stage" style={{ backgroundColor: `${stage.color}18`, color: stage.color }}>
              {stage.name}
            </span>
            <span className="deal-detail-status">Active</span>
          </div>
          <h2 className="deal-detail-name">{deal.name}</h2>
        </div>

        <div className="deal-detail-metrics">
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><DollarSign size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Value</span>
              <span className="deal-detail-metric-value">{formatCurrency(deal.value)}</span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Building2 size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Industry</span>
              <span className="deal-detail-metric-value">{deal.industry || '—'}</span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Calendar size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Target Close</span>
              <span className="deal-detail-metric-value">
                {deal.targetClose ? new Date(deal.targetClose).toLocaleDateString() : '—'}
              </span>
            </div>
          </div>
          <div className="deal-detail-metric">
            <div className="deal-detail-metric-icon"><Clock size={18} /></div>
            <div className="deal-detail-metric-content">
              <span className="deal-detail-metric-label">Owner</span>
              <span className="deal-detail-metric-value">{deal.owner || 'Unassigned'}</span>
            </div>
          </div>
        </div>

        <div className="deal-detail-tabs">
          {tabs.map(tab => (
            <button key={tab.id}
              className={`deal-detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              <tab.icon size={18} className="deal-detail-tab-icon" />
              <span className="deal-detail-tab-label">{tab.label}</span>
              {tab.count > 0 && <span className="deal-detail-tab-count">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="deal-detail-content">
        {activeTab === 'tasks' && (
          <TasksTab dealId={deal.id} tasks={tasks} onUpdate={fetchDealData} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab
            dealId={deal.id}
            documents={dealDocuments}
            allDocuments={documents}
            allDeals={allDeals}
            onUpdate={fetchDealData}
            onOpenAnnotate={onOpenAnnotate}
          />
        )}
        {activeTab === 'contacts' && (
          <ContactsTab dealId={deal.id} contacts={contacts} onUpdate={fetchDealData} />
        )}
        {activeTab === 'diligence' && (
          <DiligenceTracker selectedDeal={deal} />
        )}
        {activeTab === 'notes' && (
          <NotesTab dealId={deal.id} currentUser={currentUser} />
        )}
        {activeTab === 'intelligence' && (
          <IntelligenceTab deal={deal} />
        )}
        {activeTab === 'edit' && (
          <EditDealTab deal={deal} orgUsers={orgUsers} onUpdate={onUpdateDeal} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Edit Deal Tab
// ─────────────────────────────────────────────
function EditDealTab({ deal, orgUsers, onUpdate }) {
  const [name, setName] = useState(deal.name || '');
  const [industry, setIndustry] = useState(deal.industry || '');
  const [value, setValue] = useState(deal.value || '');
  const [targetClose, setTargetClose] = useState(deal.targetClose ? deal.targetClose.substring(0, 10) : '');
  const [stage, setStage] = useState(normaliseStage(deal.stage) || 'level_1_assessment');
  const [owner, setOwner] = useState(deal.owner || '');
  // Extended intelligence fields
  const [dealType, setDealType] = useState(deal.dealType || '');
  const [dealSource, setDealSource] = useState(deal.dealSource || '');
  const [geography, setGeography] = useState(deal.geography || '');
  const [entryRevenue, setEntryRevenue] = useState(deal.entryRevenue || '');
  const [entryEbitda, setEntryEbitda] = useState(deal.entryEbitda || '');
  const [ndaSignedDate, setNdaSignedDate] = useState(deal.ndaSignedDate ? deal.ndaSignedDate.substring(0, 10) : '');
  const [loiDate, setLoiDate] = useState(deal.loiDate ? deal.loiDate.substring(0, 10) : '');
  const [exclusivityDate, setExclusivityDate] = useState(deal.exclusivityDate ? deal.exclusivityDate.substring(0, 10) : '');
  const [ddStartDate, setDdStartDate] = useState(deal.ddStartDate ? deal.ddStartDate.substring(0, 10) : '');
  const [ddEndDate, setDdEndDate] = useState(deal.ddEndDate ? deal.ddEndDate.substring(0, 10) : '');
  const [negotiationRounds, setNegotiationRounds] = useState(deal.negotiationRounds || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onUpdate(deal.id, {
      name, industry,
      value: value ? parseFloat(value) : null,
      targetClose, stage,
      owner: owner || null,
      dealType, dealSource, geography,
      entryRevenue: entryRevenue ? parseFloat(entryRevenue) : null,
      entryEbitda: entryEbitda ? parseFloat(entryEbitda) : null,
      ndaSignedDate, loiDate, exclusivityDate, ddStartDate, ddEndDate,
      negotiationRounds: negotiationRounds ? parseInt(negotiationRounds) : null,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Edit Deal</h3>
      </div>
      <div className="deal-edit-form">
        <div className="deals-form-group">
          <label>Deal Name *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Stage</label>
            <select value={stage} onChange={e => setStage(e.target.value)}>
              {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="deals-form-group">
            <label>Owner</label>
            <select value={owner} onChange={e => setOwner(e.target.value)}>
              <option value="">Unassigned</option>
              {orgUsers.map(u => (
                <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Industry</label>
            <IndustryInput value={industry} onChange={setIndustry} />
          </div>
          <div className="deals-form-group">
            <label>Target Close</label>
            <input type="date" value={targetClose} onChange={e => setTargetClose(e.target.value)} />
          </div>
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Deal Value</label>
            <div className="deals-input-with-icon">
              <DollarSign size={16} className="deals-input-icon" />
              <input type="number" value={value} onChange={e => setValue(e.target.value)} />
            </div>
          </div>
          <div className="deals-form-group">
            <label>Geography</label>
            <input type="text" placeholder="Country / Region" value={geography} onChange={e => setGeography(e.target.value)} />
          </div>
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Deal Type</label>
            <select value={dealType} onChange={e => setDealType(e.target.value)}>
              {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="deals-form-group">
            <label>Deal Source</label>
            <select value={dealSource} onChange={e => setDealSource(e.target.value)}>
              {DEAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="deal-edit-section-title">Entry Financials</div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Entry Revenue ($)</label>
            <div className="deals-input-with-icon">
              <DollarSign size={16} className="deals-input-icon" />
              <input type="number" placeholder="Annual revenue at entry" value={entryRevenue} onChange={e => setEntryRevenue(e.target.value)} />
            </div>
          </div>
          <div className="deals-form-group">
            <label>Entry EBITDA ($)</label>
            <div className="deals-input-with-icon">
              <DollarSign size={16} className="deals-input-icon" />
              <input type="number" placeholder="EBITDA at entry" value={entryEbitda} onChange={e => setEntryEbitda(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="deal-edit-section-title">Deal Timeline</div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>NDA Signed</label>
            <input type="date" value={ndaSignedDate} onChange={e => setNdaSignedDate(e.target.value)} />
          </div>
          <div className="deals-form-group">
            <label>LOI Date</label>
            <input type="date" value={loiDate} onChange={e => setLoiDate(e.target.value)} />
          </div>
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>Exclusivity Date</label>
            <input type="date" value={exclusivityDate} onChange={e => setExclusivityDate(e.target.value)} />
          </div>
          <div className="deals-form-group">
            <label>DD Start Date</label>
            <input type="date" value={ddStartDate} onChange={e => setDdStartDate(e.target.value)} />
          </div>
        </div>
        <div className="deals-form-row">
          <div className="deals-form-group">
            <label>DD End Date</label>
            <input type="date" value={ddEndDate} onChange={e => setDdEndDate(e.target.value)} />
          </div>
          <div className="deals-form-group">
            <label>Negotiation Rounds</label>
            <input type="number" min="0" placeholder="# of rounds" value={negotiationRounds} onChange={e => setNegotiationRounds(e.target.value)} />
          </div>
        </div>

        <div className="deal-edit-actions">
          <button className="deals-btn-primary" onClick={handleSave}
            disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tasks Tab
// ─────────────────────────────────────────────
function TasksTab({ dealId, tasks, onUpdate }) {
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await dealsApi.createTask(dealId, { title: newTaskTitle, status: 'pending' });
      setNewTaskTitle('');
      setShowAddTask(false);
      onUpdate();
    } catch (err) { console.error('Failed to add task:', err); }
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Tasks ({tasks.length})</h3>
        <button className="deal-btn-add" onClick={() => setShowAddTask(true)}>
          <Plus size={16} />Add Task
        </button>
      </div>
      {showAddTask && (
        <div className="deal-add-item-row">
          <input type="text" placeholder="Enter task description..."
            value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleAddTask()} autoFocus />
          <button onClick={handleAddTask}>Add</button>
          <button className="deal-btn-cancel" onClick={() => setShowAddTask(false)}>Cancel</button>
        </div>
      )}
      {tasks.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><CheckSquare size={40} /></div>
          <h4>No tasks</h4><p>Add tasks to track deal work</p>
        </div>
      ) : (
        <div className="deal-tasks-list">
          {tasks.map(task => (
            <div key={task.id} className={`deal-task-item ${task.status}`}>
              <input type="checkbox" checked={task.status === 'completed'}
                onChange={async () => {
                  await dealsApi.updateTask(dealId, task.id,
                    { status: task.status === 'completed' ? 'pending' : 'completed' });
                  onUpdate();
                }} />
              <span className="deal-task-title">{task.title}</span>
              <span className="deal-task-date">{new Date(task.createdAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Documents Tab
// ─────────────────────────────────────────────
function DocumentsTab({ dealId, documents, allDocuments, allDeals, onUpdate, onOpenAnnotate }) {
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  const docIdsInOtherDeals = allDeals
    .filter(d => d.id !== dealId)
    .flatMap(d => d.documents || []);

  const unassignedDocs = allDocuments.filter(doc =>
    !documents.some(d => d.jobId === doc.jobId) &&
    !docIdsInOtherDeals.includes(doc.jobId)
  );

  const toggleDocSelection = (docId) =>
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );

  const handleAddDocuments = async () => {
    if (selectedDocIds.length === 0) return;
    setIsAdding(true);
    try {
      for (const docId of selectedDocIds)
        await dealsApi.addDocument(dealId, docId);
      setSelectedDocIds([]);
      setShowAddDoc(false);
      onUpdate();
    } catch (err) { console.error('Failed to add documents:', err); }
    finally { setIsAdding(false); }
  };

  const selectAll = () =>
    setSelectedDocIds(
      selectedDocIds.length === unassignedDocs.length ? [] : unassignedDocs.map(d => d.jobId)
    );

  const handleRemoveDocument = async (docId) => {
    try {
      await dealsApi.removeDocument(dealId, docId);
      onUpdate();
    } catch (err) { console.error('Failed to remove document:', err); }
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Documents ({documents.length})</h3>
        <button className="deal-btn-add" onClick={() => setShowAddDoc(true)}>
          <Plus size={16} />Add Document
        </button>
      </div>
      {showAddDoc && (
        <div className="deal-add-documents-panel">
          <div className="deal-add-documents-header">
            <label className="deal-select-all">
              <input type="checkbox"
                checked={selectedDocIds.length === unassignedDocs.length && unassignedDocs.length > 0}
                onChange={selectAll} />
              <span>Select All ({unassignedDocs.length})</span>
            </label>
            <span className="deal-selected-count">{selectedDocIds.length} selected</span>
          </div>
          <div className="deal-documents-select-list">
            {unassignedDocs.length === 0
              ? <p className="deal-no-docs-message">No unassigned documents available</p>
              : unassignedDocs.map(doc => (
                <label key={doc.jobId} className="deal-doc-checkbox">
                  <input type="checkbox" checked={selectedDocIds.includes(doc.jobId)}
                    onChange={() => toggleDocSelection(doc.jobId)} />
                  <FileText size={20} className="deal-doc-checkbox-icon" />
                  <div className="deal-doc-checkbox-info">
                    <span className="deal-doc-checkbox-name">{doc.filename}</span>
                    <span className="deal-doc-checkbox-type">{doc.documentType}</span>
                  </div>
                </label>
              ))
            }
          </div>
          <div className="deal-add-documents-actions">
            <button className="deal-btn-cancel"
              onClick={() => { setShowAddDoc(false); setSelectedDocIds([]); }}>Cancel</button>
            <button onClick={handleAddDocuments}
              disabled={selectedDocIds.length === 0 || isAdding} className="deal-btn-add">
              {isAdding ? 'Adding...' : `Add ${selectedDocIds.length} Document${selectedDocIds.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
      {documents.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><FileText size={40} /></div>
          <h4>No documents</h4><p>Add documents to this deal</p>
        </div>
      ) : (
        <div className="deal-documents-list">
          {documents.map(doc => (
            <div key={doc.jobId} className="deal-document-item">
              <div className="deal-document-info" onClick={() => onOpenAnnotate?.(doc)}>
                <File size={20} className="deal-document-icon" />
                <div className="deal-document-details">
                  <span className="deal-document-name">{doc.filename}</span>
                  <span className="deal-document-meta">
                    {doc.documentType} • {new Date(doc.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <button className="deal-document-remove"
                onClick={() => handleRemoveDocument(doc.jobId)} title="Remove from deal">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Contacts Tab
// ─────────────────────────────────────────────
function ContactsTab({ dealId, contacts, onUpdate }) {
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', role: '', email: '' });

  const handleAddContact = async () => {
    if (!newContact.name.trim()) return;
    try {
      await dealsApi.create(dealId, newContact);
      setNewContact({ name: '', role: '', email: '' });
      setShowAddContact(false);
      onUpdate();
    } catch (err) { console.error('Failed to add contact:', err); }
  };

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Contacts ({contacts.length})</h3>
        <button className="deal-btn-add" onClick={() => setShowAddContact(true)}>
          <Plus size={16} />Add Contact
        </button>
      </div>
      {showAddContact && (
        <div className="deal-add-contact-form">
          <input type="text" placeholder="Name *" value={newContact.name}
            onChange={e => setNewContact({ ...newContact, name: e.target.value })} />
          <input type="text" placeholder="Role" value={newContact.role}
            onChange={e => setNewContact({ ...newContact, role: e.target.value })} />
          <input type="email" placeholder="Email" value={newContact.email}
            onChange={e => setNewContact({ ...newContact, email: e.target.value })} />
          <div className="deal-add-contact-actions">
            <button onClick={handleAddContact} disabled={!newContact.name.trim()}>Add</button>
            <button className="deal-btn-cancel" onClick={() => setShowAddContact(false)}>Cancel</button>
          </div>
        </div>
      )}
      {contacts.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><Users size={40} /></div>
          <h4>No contacts</h4><p>Add contacts for this deal</p>
        </div>
      ) : (
        <div className="deal-contacts-list">
          {contacts.map(contact => (
            <div key={contact.id} className="deal-contact-item">
              <div className="deal-contact-avatar">{contact.name?.charAt(0).toUpperCase()}</div>
              <div className="deal-contact-info">
                <span className="deal-contact-name">{contact.name}</span>
                {contact.role && <span className="deal-contact-role">{contact.role}</span>}
                {contact.email && <span className="deal-contact-email">{contact.email}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Notes Tab
// ─────────────────────────────────────────────
function NotesTab({ dealId, currentUser }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchNotes(); }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotes = async () => {
    try {
      const res = await dealsApi.getNotes(dealId);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const authorName = currentUser?.name || currentUser?.email || 'Unknown';
      const res = await dealsApi.addNote(dealId, newNote.trim());
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await dealsApi.deleteNote(dealId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const formatTs = (ts) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Notes ({notes.length})</h3>
      </div>

      {/* Compose box */}
      <div className="notes-compose">
        <textarea
          className="notes-textarea"
          placeholder="Write a note..."
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
          rows={3}
        />
        <div className="notes-compose-footer">
          <span className="notes-hint">Cmd+Enter to submit</span>
          <button
            className="deals-btn-primary notes-submit-btn"
            onClick={handleAddNote}
            disabled={saving || !newNote.trim()}
          >
            <Send size={14} /> {saving ? 'Saving…' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <div className="deal-empty-state">
          <div className="deal-empty-icon"><StickyNote size={40} /></div>
          <h4>No notes yet</h4>
          <p>Add a note to keep track of important details</p>
        </div>
      ) : (
        <div className="notes-list">
          {notes.map(note => (
            <div key={note.id} className="note-item">
              <div className="note-header">
                <div className="note-author-row">
                  <span className="note-avatar">{note.authorName?.[0]?.toUpperCase() || '?'}</span>
                  <span className="note-author">{note.authorName}</span>
                  <span className="note-time">{formatTs(note.createdAt)}</span>
                </div>
                <button
                  className="note-delete-btn"
                  onClick={() => handleDeleteNote(note.id)}
                  title="Delete note"
                >
                  <X size={14} />
                </button>
              </div>
              <p className="note-text">{note.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Outcome Exit Modal
// Shown when a deal is moved to archived or discontinued
// ─────────────────────────────────────────────
function OutcomeExitModal({ deal, targetStage, onSubmit, onCancel }) {
  const [outcome, setOutcome] = useState(targetStage === 'discontinued' ? 'passed' : 'closed');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [outcomeDetails, setOutcomeDetails] = useState('');
  const [finalValue, setFinalValue] = useState(deal.value || '');
  const [dealQualityScore, setDealQualityScore] = useState('');
  const [teamNotes, setTeamNotes] = useState('');

  const reasons = OUTCOME_REASONS[outcome] || [];

  return (
    <div className="deals-modal-overlay" onClick={onCancel}>
      <div className="deals-modal deals-outcome-modal" onClick={e => e.stopPropagation()}>
        <div className="deals-modal-header">
          <div className="deals-modal-title-row">
            <Target size={20} />
            <h2>Capture Deal Outcome</h2>
          </div>
          <button className="deals-modal-close" onClick={onCancel}><X size={20} /></button>
        </div>

        <div className="deals-modal-body">
          <p className="deals-outcome-intro">
            Before archiving <strong>"{deal.name}"</strong>, capture the outcome. This data improves your team's deal intelligence over time.
          </p>

          <div className="deals-form-group">
            <label>Outcome *</label>
            <div className="outcome-options">
              {[
                { value: 'closed',          label: '✓  Deal Closed',          color: '#10b981' },
                { value: 'passed',          label: '↩  Passed / Declined',    color: '#f59e0b' },
                { value: 'lost_competitor', label: '✗  Lost to Competitor',   color: '#ef4444' },
                { value: 'deal_died',       label: '—  Deal Died',            color: '#6b7280' },
              ].map(o => (
                <button key={o.value}
                  type="button"
                  className={`outcome-option-btn ${outcome === o.value ? 'selected' : ''}`}
                  style={outcome === o.value ? { borderColor: o.color, color: o.color, background: `${o.color}10` } : {}}
                  onClick={() => { setOutcome(o.value); setOutcomeReason(''); }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {reasons.length > 0 && (
            <div className="deals-form-group">
              <label>Primary Reason</label>
              <select value={outcomeReason} onChange={e => setOutcomeReason(e.target.value)}>
                <option value="">Select reason…</option>
                {reasons.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}

          <div className="deals-form-group">
            <label>Additional Notes</label>
            <textarea
              rows={3}
              placeholder="What happened? Key factors, lessons learned…"
              value={outcomeDetails}
              onChange={e => setOutcomeDetails(e.target.value)}
              className="deals-outcome-textarea"
            />
          </div>

          <div className="deals-form-row">
            <div className="deals-form-group">
              <label>Final Value ($)</label>
              <div className="deals-input-with-icon">
                <DollarSign size={16} className="deals-input-icon" />
                <input type="number" placeholder="Actual transaction value"
                  value={finalValue} onChange={e => setFinalValue(e.target.value)} />
              </div>
            </div>
            <div className="deals-form-group">
              <label>Deal Quality Score (1–5)</label>
              <select value={dealQualityScore} onChange={e => setDealQualityScore(e.target.value)}>
                <option value="">Rate overall quality…</option>
                {[1,2,3,4,5].map(n => (
                  <option key={n} value={n}>{n} — {['Very Poor','Poor','Average','Good','Excellent'][n-1]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="deals-form-group">
            <label>Internal Team Notes</label>
            <textarea
              rows={2}
              placeholder="Private notes for your team (not shared externally)…"
              value={teamNotes}
              onChange={e => setTeamNotes(e.target.value)}
              className="deals-outcome-textarea"
            />
          </div>
        </div>

        <div className="deals-modal-footer">
          <button className="deals-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="deals-btn-primary"
            onClick={() => onSubmit(deal, targetStage, { outcome, outcomeReason, outcomeDetails, finalValue, dealQualityScore, teamNotes })}
          >
            <Award size={16} /> Save & Archive Deal
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Intelligence Tab — shows captured deal data points
// ─────────────────────────────────────────────
function IntelligenceTab({ deal }) {
  const fmt = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v) : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const multiple = deal.finalValue && deal.value ? (deal.finalValue / deal.value).toFixed(2) : null;

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>Deal Intelligence</h3>
      </div>

      <div className="intel-grid">
        {/* Classification */}
        <div className="intel-card">
          <div className="intel-card-title"><TrendingUp size={14} /> Classification</div>
          <div className="intel-row"><span>Type</span><strong>{deal.dealType?.replace(/_/g,' ') || '—'}</strong></div>
          <div className="intel-row"><span>Source</span><strong>{deal.dealSource?.replace(/_/g,' ') || '—'}</strong></div>
          <div className="intel-row"><span>Geography</span><strong>{deal.geography || '—'}</strong></div>
        </div>

        {/* Entry Financials */}
        <div className="intel-card">
          <div className="intel-card-title"><DollarSign size={14} /> Entry Financials</div>
          <div className="intel-row"><span>Entry Value</span><strong>{fmt(deal.value)}</strong></div>
          <div className="intel-row"><span>Revenue</span><strong>{fmt(deal.entryRevenue)}</strong></div>
          <div className="intel-row"><span>EBITDA</span><strong>{fmt(deal.entryEbitda)}</strong></div>
          {deal.entryRevenue && deal.value && (
            <div className="intel-row"><span>EV/Revenue</span><strong>{(deal.value / deal.entryRevenue).toFixed(1)}x</strong></div>
          )}
          {deal.entryEbitda && deal.value && (
            <div className="intel-row"><span>EV/EBITDA</span><strong>{(deal.value / deal.entryEbitda).toFixed(1)}x</strong></div>
          )}
        </div>

        {/* Timeline */}
        <div className="intel-card">
          <div className="intel-card-title"><Calendar size={14} /> Timeline</div>
          <div className="intel-row"><span>Created</span><strong>{fmtDate(deal.createdAt)}</strong></div>
          <div className="intel-row"><span>NDA Signed</span><strong>{fmtDate(deal.ndaSignedDate)}</strong></div>
          <div className="intel-row"><span>LOI</span><strong>{fmtDate(deal.loiDate)}</strong></div>
          <div className="intel-row"><span>Exclusivity</span><strong>{fmtDate(deal.exclusivityDate)}</strong></div>
          <div className="intel-row"><span>DD Start</span><strong>{fmtDate(deal.ddStartDate)}</strong></div>
          <div className="intel-row"><span>DD End</span><strong>{fmtDate(deal.ddEndDate)}</strong></div>
          {deal.ddStartDate && deal.ddEndDate && (
            <div className="intel-row"><span>DD Duration</span><strong>{Math.round((new Date(deal.ddEndDate) - new Date(deal.ddStartDate)) / 86400000)} days</strong></div>
          )}
        </div>

        {/* Outcome */}
        {deal.outcome && (
          <div className="intel-card">
            <div className="intel-card-title"><Award size={14} /> Outcome</div>
            <div className="intel-row"><span>Result</span><strong style={{ textTransform: 'capitalize' }}>{deal.outcome?.replace(/_/g,' ') || '—'}</strong></div>
            {deal.outcomeReason && <div className="intel-row"><span>Reason</span><strong>{deal.outcomeReason}</strong></div>}
            {deal.finalValue && <div className="intel-row"><span>Final Value</span><strong>{fmt(deal.finalValue)}</strong></div>}
            {multiple && <div className="intel-row"><span>Value vs Target</span><strong>{multiple}x</strong></div>}
            {deal.dealQualityScore && <div className="intel-row"><span>Quality Score</span><strong>{deal.dealQualityScore}/5</strong></div>}
            {deal.negotiationRounds && <div className="intel-row"><span>Negotiation Rounds</span><strong>{deal.negotiationRounds}</strong></div>}
            {deal.outcomeDetails && (
              <div className="intel-notes">
                <span className="intel-notes-label">Notes</span>
                <p>{deal.outcomeDetails}</p>
              </div>
            )}
          </div>
        )}

        {!deal.outcome && (
          <div className="intel-card intel-card-empty">
            <div className="intel-card-title"><Award size={14} /> Outcome</div>
            <p className="intel-empty-text">Outcome will be captured when this deal is archived or discontinued.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Deals;
